import React, { useEffect, useRef, useState } from "react";
import { VOICE_STATUS } from "../../hooks/useVoiceStorybookSession";

const FREDOKA = { fontFamily: "'Fredoka', sans-serif" };

/**
 * Compact floating control for the AI Voice Storybook — overlaid near
 * the bottom-center of the book itself (not a separate panel/layout). All
 * connection/audio/event logic lives in useVoiceStorybookSession; this is
 * purely presentational.
 */
const VoiceSessionPanel = ({
  status,
  isMicOn,
  isAssistantSpeaking = false,
  isCreatingPage = false,
  onToggleMic,
  errorMessage,
  onStart,
  onReconnect,
  onSendText,
  onEndStory,
}) => {
  const [showTextInput, setShowTextInput] = useState(false);
  const [textDraft, setTextDraft] = useState("");
  // End button: two taps (End → End story?) so one stray tap can't end the
  // session. endRequested flips the main pill to "Saying goodbye…" until the
  // backend's session_complete unmounts this panel.
  const [confirmEnd, setConfirmEnd] = useState(false);
  const [endRequested, setEndRequested] = useState(false);
  const confirmEndTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (confirmEndTimerRef.current) clearTimeout(confirmEndTimerRef.current);
    };
  }, []);

  const handleEndClick = () => {
    if (endRequested) return;
    if (!confirmEnd) {
      setConfirmEnd(true);
      confirmEndTimerRef.current = setTimeout(() => setConfirmEnd(false), 3500);
      return;
    }
    if (confirmEndTimerRef.current) clearTimeout(confirmEndTimerRef.current);
    setConfirmEnd(false);
    setEndRequested(true);
    onEndStory?.();
  };

  const isIdle = status === VOICE_STATUS.IDLE;
  const isConnecting = status === VOICE_STATUS.CONNECTING;
  const isLive = status === VOICE_STATUS.LIVE;
  const isErrored = status === VOICE_STATUS.ERROR;
  const isReplaced = status === VOICE_STATUS.REPLACED;
  const isPausedUpgrade = status === VOICE_STATUS.PAUSED_UPGRADE;
  const isComplete = status === VOICE_STATUS.COMPLETE;

  let label = "Start Talking With Your Story";
  let tone = "cta"; // cta | live | muted | busy | error
  let clickable = true;
  let onClick = onStart;

  if (isConnecting) {
    label = "Connecting…";
    tone = "busy";
    clickable = false;
  } else if (isErrored) {
    label = errorMessage || "Reconnecting…";
    tone = "error";
    onClick = onReconnect;
  } else if (isReplaced) {
    label = "Open in another tab";
    tone = "error";
    clickable = false;
  } else if (isComplete) {
    // Between session_complete and the player unmounting this panel — never
    // flash the start CTA over a finished story.
    label = "The End! 🎉";
    tone = "cta";
    clickable = false;
  } else if (endRequested && (isLive || isPausedUpgrade)) {
    label = "Saying goodbye…";
    tone = "busy";
    clickable = false;
  } else if (isPausedUpgrade) {
    label = "Story paused…";
    tone = "busy";
    clickable = false;
  } else if (isLive) {
    clickable = true;
    onClick = onToggleMic;
    if (isCreatingPage) {
      label = "Creating page…";
      tone = "busy";
      clickable = false;
    } else if (isAssistantSpeaking) {
      label = "Pixley is thinking…";
      tone = "busy";
      clickable = false;
    } else if (isMicOn) {
      label = "Listening…";
      tone = "live";
    } else {
      label = "Tap to talk";
      tone = "muted";
    }
  }

  const toneStyles = {
    cta: { background: "#0065F8", color: "#FFFFFF", shadow: "0px 4px 0px #073B8E, 0px 12px 24px rgba(0,101,248,0.28)" },
    live: { background: "#10B981", color: "#FFFFFF", shadow: "0px 4px 0px #06724D, 0px 12px 24px rgba(16,185,129,0.30)" },
    muted: { background: "#FFFFFF", color: "#0B1A33", shadow: "0px 3px 0px rgba(11,26,51,0.10)" },
    busy: { background: "#F59E0B", color: "#FFFFFF", shadow: "0px 4px 0px #92600A" },
    error: { background: "#DC2626", color: "#FFFFFF", shadow: "0px 4px 0px #7F1D1D" },
  }[tone];

  const handleSendText = () => {
    if (!textDraft.trim()) return;
    onSendText?.(textDraft);
    setTextDraft("");
  };

  return (
    <div className="flex flex-col items-center gap-2 pointer-events-none">
      {showTextInput && isLive && !endRequested && (
        <div
          className="flex items-center gap-2 pointer-events-auto"
          style={{
            background: "#FFFFFF",
            border: "2px solid rgba(11,26,51,0.10)",
            borderRadius: 999,
            boxShadow: "0px 3px 0px rgba(11,26,51,0.08), 0px 10px 24px rgba(11,26,51,0.16)",
            padding: "6px 8px 6px 16px",
          }}
        >
          <input
            type="text"
            value={textDraft}
            onChange={(e) => setTextDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleSendText(); }}
            placeholder="Type your answer…"
            autoFocus
            className="focus:outline-none"
            style={{ ...FREDOKA, fontSize: 13, color: "#0B1A33", width: 180 }}
          />
          <button
            type="button"
            onClick={handleSendText}
            style={{ ...FREDOKA, background: "#0065F8", color: "#FFFFFF", fontWeight: 700, fontSize: 12, border: "none", borderRadius: 999, padding: "8px 14px", cursor: "pointer" }}
          >
            Send
          </button>
        </div>
      )}

      <div className="flex items-center gap-2 pointer-events-auto">
        <button
          type="button"
          onClick={clickable ? onClick : undefined}
          disabled={!clickable}
          aria-label={label}
          className="inline-flex items-center gap-2 transition-all active:scale-[0.97]"
          style={{
            ...FREDOKA,
            background: toneStyles.background,
            color: toneStyles.color,
            fontWeight: 700,
            fontSize: 14,
            border: "none",
            borderRadius: 999,
            padding: isIdle ? "14px 24px" : "11px 18px",
            boxShadow: toneStyles.shadow,
            cursor: clickable ? "pointer" : "default",
            opacity: clickable || tone === "busy" || tone === "error" ? 1 : 0.85,
            animation: tone === "live" ? "voiceMicPulse 1.4s ease-in-out infinite" : "none",
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </button>

        {(isLive || isPausedUpgrade) && !endRequested && (
          <button
            type="button"
            onClick={handleEndClick}
            aria-label={confirmEnd ? "Tap again to end the story" : "End the story"}
            className="inline-flex items-center justify-center transition active:scale-[0.95]"
            style={{
              height: 36,
              borderRadius: 999,
              padding: "0 14px",
              background: confirmEnd ? "#DC2626" : "#FFFFFF",
              color: confirmEnd ? "#FFFFFF" : "#DC2626",
              border: confirmEnd
                ? "2px solid #DC2626"
                : "2px solid rgba(220,38,38,0.25)",
              boxShadow: confirmEnd
                ? "0px 2px 0px #7F1D1D"
                : "0px 2px 0px rgba(11,26,51,0.06)",
              cursor: "pointer",
              ...FREDOKA,
              fontSize: 12,
              fontWeight: 700,
              whiteSpace: "nowrap",
            }}
          >
            {confirmEnd ? "End story?" : "End"}
          </button>
        )}

        {isLive && !endRequested && (
          <button
            type="button"
            onClick={() => setShowTextInput((v) => !v)}
            aria-label="Type instead of talking"
            className="inline-flex items-center justify-center transition active:scale-[0.95]"
            style={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              background: "#FFFFFF",
              color: "#5A6B85",
              border: "2px solid rgba(11,26,51,0.10)",
              boxShadow: "0px 2px 0px rgba(11,26,51,0.06)",
              cursor: "pointer",
              ...FREDOKA,
              fontSize: 11,
              fontWeight: 700,
            }}
          >
            Aa
          </button>
        )}
      </div>

      <style>{`
        @keyframes voiceMicPulse {
          0%, 100% { box-shadow: 0px 4px 0px #06724D, 0 0 0 0 rgba(16,185,129,0.35); }
          50% { box-shadow: 0px 4px 0px #06724D, 0 0 0 10px rgba(16,185,129,0); }
        }
      `}</style>
    </div>
  );
};

export default VoiceSessionPanel;
